/**
 * Draft persistence for an ownership claim typed by a signed-out operator.
 *
 * Signing in from the claim modal is a FULL-PAGE redirect to the owner portal,
 * so the modal's React state dies on the way out. Without a stash the operator
 * fills in the whole claim, presses Submit, gets bounced to sign-in, comes back
 * to an empty page — and reasonably believes the claim was lodged. Nothing was
 * ever POSTed: the graph held zero :Claim nodes while operators told the chat
 * assistant they had claimed their site.
 *
 * Same shape as the review (`pendingReview`) and suggest-a-campsite
 * (`pendingSubmission`) drafts: sessionStorage, so it survives the OAuth round
 * trip in the same tab and does not linger on a shared machine.
 *
 * Files are deliberately NOT stashed — `File` is not serialisable, and base64ing
 * evidence photos into sessionStorage risks blowing the quota. The modal tells
 * the operator to re-attach them.
 */

export interface ClaimFormData {
  claim_type: string;
  business_name: string;
  contact_phone: string;
  contact_email: string;
  website: string;
  business_registration: string;
  evidence_description: string;
  relationship_to_site: string;
  message: string;
}

export interface PendingClaim {
  entityId: string | number;
  form: ClaimFormData;
  savedAt: number;
}

export const PENDING_CLAIM_KEY = 'pendingClaim';
export const PENDING_CLAIM_TTL_MS = 24 * 60 * 60 * 1000;

export const EMPTY_CLAIM_FORM: ClaimFormData = {
  claim_type: 'ownership',
  business_name: '',
  contact_phone: '',
  contact_email: '',
  website: '',
  business_registration: '',
  evidence_description: '',
  relationship_to_site: 'owner',
  message: '',
};

const store = (): Storage | null => {
  // Guard on the API itself, not on `window`: server renders have neither, and
  // a browser with storage disabled throws on access rather than reporting it.
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
};

export function savePendingClaim(entityId: string | number, form: ClaimFormData): void {
  const s = store();
  if (!s) return;
  try {
    const pending: PendingClaim = { entityId, form, savedAt: Date.now() };
    s.setItem(PENDING_CLAIM_KEY, JSON.stringify(pending));
  } catch {
    // Quota or privacy mode. The sign-in redirect still has to happen — losing
    // the draft is bad, blocking the claim entirely is worse.
  }
}

export function readPendingClaim(entityId: string | number): PendingClaim | null {
  const s = store();
  if (!s) return null;
  try {
    const raw = s.getItem(PENDING_CLAIM_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as PendingClaim;
    if (!pending || !pending.form) return null;
    // Entity ids arrive as number from the API and string from route params.
    if (String(pending.entityId) !== String(entityId)) return null;
    if (!pending.savedAt || Date.now() - pending.savedAt > PENDING_CLAIM_TTL_MS) return null;
    return pending;
  } catch {
    return null;
  }
}

export function clearPendingClaim(): void {
  const s = store();
  if (!s) return;
  try {
    s.removeItem(PENDING_CLAIM_KEY);
  } catch {
    // ignore
  }
}
